import { Unarchive, Archive, Edit, Save, Close } from '@mui/icons-material';
import {
  Divider,
  IconButton,
  Typography,
  Grid,
  CircularProgress,
  TextField,
  Paper,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  forwardRef,
  useEffect,
  useState,
  useImperativeHandle,
  useRef,
} from 'react';
import moment from 'moment';
import { AlertDialog } from '../dialogs';
import { ButtonMenu, ButtonMenuProps } from './ButtonMenu';
import { styled } from '@mui/material/styles';
import {
  UseMutation,
  UseLazyQuery,
  UseQueryState,
} from '@reduxjs/toolkit/dist/query/react/buildHooks';

export interface GroupingProps {
  documentId?: string;
  data?: any;
  reloadParent?: Function;
  key?: string;
  status?: ButtonMenuProps;
  selected?: boolean;
  highlighted?: boolean;
  numChildren?: number;
  childrenPastDue?: number;
  childrenAtRisk?: number;
  statusCount?: { [key: string]: number };
  color?: 'ontrack' | 'complete' | 'warning' | 'pastdue' | 'default';
  showDetails?: boolean;
  disabled?: boolean;
  expandParent?: Function;
  patchMutation?: UseMutation<any>;
  subscription?: UseQueryState<any>;
  lazyQuery?: UseLazyQuery<any>;
  queryParams?: object;
}

export const Grouping = forwardRef((props: GroupingProps, ref) => {
  const {
    documentId,
    data,
    reloadParent = () => {},
    key,
    status,
    selected,
    highlighted = false,
    numChildren = 0,
    childrenPastDue = 0,
    childrenAtRisk = 0,
    statusCount,
    color = 'default',
    showDetails = false,
    disabled = false,
    expandParent,
    patchMutation,
    subscription,
    lazyQuery,
    queryParams,
  } = props;

  const [value, setValue] = useState<string>(data?.title || '');
  const [groupingStatus, setStatus] = useState<string>(data?.status || '');
  const [hover, setHover] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [prevValue, setPrevValue] = useState<string>(data?.title || '');
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [patch, patchResult] = (patchMutation && patchMutation()) || [
    () => {},
    {},
  ];
  const [reload, reloadResult] = (lazyQuery && lazyQuery()) || [() => {}];
  const { data: groupingCache } =
    (subscription && queryParams && subscription(queryParams)) || {};
  const triggerReload = async () => {
    if (queryParams) {
      await reload(queryParams);
    }
  };
  useEffect(() => {
    const item: any = patchResult?.data;
    if (item) {
      setValue((pv: string) => item?.title || pv);
      setStatus((pv: string) => item?.status || pv);
    }
  }, [patchResult]);

  useEffect(() => {
    const item: any = groupingCache;
    if (item?.id) {
      setValue((pv: string) => item?.title || pv);
      setStatus((pv: string) => item?.status || pv);
    }
  }, [groupingCache]);

  const scrollRef = useRef<HTMLElement>(null);
  const scrollToRef = () => {
    if (scrollRef && scrollRef.current) {
      scrollRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useImperativeHandle(ref, () => ({
    expandParent: () => {
      expandParent && expandParent();
    },
    delete: () => {
      setOpen(true);
    },
    triggerReload: () => {
      triggerReload();
    },
    scrollRef: scrollRef,
  }));

  return (
    <>
      <Paper
        id={documentId}
        ref={ref as any}
        elevation={highlighted ? 10 : 0}
        sx={{
          zIndex: selected === false ? -1 : undefined,
          width: '100%',
          pl: highlighted ? 1 : 0,
          pr: highlighted ? 1 : { xs: 0, sm: 1 },
          py: 0,
          backgroundColor: '#ffffff1f',
          boxShadow: highlighted
            ? '0px 6px 6px -3px rgb(118, 176, 65,0.8), 0px 10px 14px 1px rgb(118, 176, 65, 0.5), 0px 4px 18px 3px rgb(118, 176, 65, 0.5)'
            : '',
        }}
        key={key}
      >
        <span ref={scrollRef}></span>
        <Grid container alignItems="center" rowSpacing={0}>
          <Grid
            item
            container
            rowSpacing={1}
            sx={{ width: { xs: '100%', sm: 'fit-content' }, mr: 'auto' }}
            onMouseEnter={() => setHover(true && !disabled && data.id > 0)}
            onMouseLeave={() => setHover(false)}
          >
            <Grid
              item
              sx={isEditing ? { width: { xs: '100%', sm: 'fit-content' } } : {}}
            >
              {isEditing ? (
                <StyledTextField
                  fullWidth
                  onClick={event => {
                    event.stopPropagation();
                    event.preventDefault();
                  }}
                  InputProps={{
                    sx: {
                      fontSize: 14,
                      fontWeight: 500,
                      height: 33,
                    },
                  }}
                  value={value}
                  onChange={(event: any) => setValue(event.target.value)}
                />
              ) : (
                <Typography
                  // onClick={onClick}
                  variant={'subtitle2'}
                  color="primary"
                  sx={{
                    height: 33,
                    lineHeight: '33px',
                    verticalAlign: 'middle',
                  }}
                >
                  {value}&nbsp;
                </Typography>
              )}
            </Grid>

            <Grid item>
              <Grid
                container
                direction="row"
                justifyContent="start"
                alignItems="center"
                wrap="nowrap"
              >
                {patchMutation && (hover || isEditing) && (
                  <IconButton
                    size="small"
                    sx={{
                      marginLeft: 0.5,
                    }}
                    onClick={event => {
                      event.stopPropagation();
                      event.preventDefault();
                      if (isEditing) {
                        patch({ title: value, id: data?.id });
                        setIsEditing(false);
                      } else {
                        setPrevValue(value);
                        setIsEditing(true);
                      }
                    }}
                  >
                    {patchResult.isLoading ? (
                      <CircularProgress size={20} />
                    ) : (
                      <>
                        {isEditing ? (
                          <Save fontSize="small" />
                        ) : (
                          <Edit fontSize="small" />
                        )}
                      </>
                    )}
                  </IconButton>
                )}
                {/* {isEditing && patchMutation && (
                  <IconButton
                    size="small"
                    sx={{
                      marginLeft: 0.5,
                    }}
                    onClick={event => {
                      event.stopPropagation();
                      event.preventDefault();
                      if (isEditing) {
                        setOpen(true);
                      }
                    }}
                  >
                    {patchResult.isLoading ? (
                      <CircularProgress size={20} />
                    ) : data?.archived ? (
                      <Unarchive fontSize="small" />
                    ) : (
                      <Archive fontSize="small" />
                    )}
                  </IconButton>
                )} */}
                {isEditing && (
                  <IconButton
                    size="small"
                    sx={{
                      marginLeft: 0.5,
                    }}
                    onClick={event => {
                      event.stopPropagation();
                      event.preventDefault();
                      setValue(prevValue);
                      setIsEditing(false);
                    }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                )}
              </Grid>
            </Grid>
          </Grid>

          {((showDetails && childrenPastDue !== 0) ||
            (showDetails && childrenAtRisk !== 0) ||
            (showDetails && numChildren !== 0) ||
            status) && (
            <Grid item sx={{ width: { xs: '100%', sm: 'fit-content' } }}>
              <Grid container alignItems="center" spacing={1}>
                {showDetails &&
                  status?.label !== 'Complete' &&
                  statusCount &&
                  Object.keys(statusCount).length > 0 && (
                    <Grid item xs={12} sm="auto">
                      <Tooltip
                        title={
                          <span>
                            {`${childrenPastDue} Deadline${
                              childrenPastDue > 1 ? 's' : ''
                            } Missed`}
                            <br />
                            {`${childrenAtRisk} Deadline${
                              childrenAtRisk > 1 ? 's' : ''
                            } Approaching`}
                            <br />
                            {`${statusCount?.['Complete'] || 0} Completed`}
                          </span>
                        }
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight:
                              childrenPastDue > 0 || childrenAtRisk > 0
                                ? 700
                                : undefined,
                            color: theme =>
                              childrenPastDue > 0
                                ? theme.palette.error.main
                                : childrenAtRisk > 0
                                ? theme.palette.warning.main
                                : 'inherit',
                          }}
                        >
                          <span style={{ color: 'black', fontWeight: 400 }}>{`${
                            statusCount?.['Complete'] || 0
                          }/`}</span>
                          {`${Object.values(statusCount)?.reduce(
                            (acc, b) => acc + b,
                            0
                          )}`}
                        </Typography>
                      </Tooltip>
                    </Grid>
                  )}
                {/* {status && (
                  <Grid item xs={12} sm="auto">
                    <ButtonMenu
                      {...status}
                      label={groupingStatus || ''}
                      onClick={async (status: any) => {
                        await patch({
                          id: Number(data?.id),
                          status: status,
                        });
                        if (
                          groupingStatus === 'Complete' ||
                          status === 'Complete'
                        ) {
                          reloadParent();
                        }
                      }}
                      sx={{ width: '100%' }}
                    />
                  </Grid>
                )} */}
                {/* {date && (
                  <Grid item xs={12} sm="auto">
                    <Typography
                      variant={'subtitle2'}
                      color="primary"
                      sx={{ width: '100%' }}
                    >{`Due: ${moment(date).format('MMM D YYYY')}`}</Typography>
                  </Grid>
                )} */}
              </Grid>
            </Grid>
          )}

          <Grid item xs={12}>
            <Divider />
          </Grid>
        </Grid>
      </Paper>

      {/* <AlertDialog
        open={open}
        title={data?.archived ? 'Unarchive Phase?' : 'Archive Phase?'}
        content={'This will also remove nested items from view'}
        onClose={() => {
          setOpen(false);
          setIsEditing(false);
        }}
        onConfirm={async () => {
          await patch({
            id: data?.id,
            archived: !data?.archived,
          });
          reloadParent();
          setOpen(false);
          setIsEditing(false);
        }}
      /> */}
    </>
  );
});

const StyledTextField = styled(TextField)(({ theme }) => ({
  input: {
    padding: '5px 5px',
  },
}));
